import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
`;

const BackButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const SongTitle = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  text-align: center;
`;

const ControlsContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const AmountSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
`;

const AmountControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin: 2rem 0;
`;

const AmountButton = styled.button`
  background: ${props => props.primary ? '#cc0000' : '#444'};
  color: white;
  border: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? '#aa0000' : '#555'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AmountDisplay = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  min-width: 120px;
  text-align: center;
`;

const ConfirmButton = styled.button`
  background: #00aa00;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 1rem;
  
  &:hover {
    background: #008800;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #cc0000;
`;

function FolderSongPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentAmount, setCurrentAmount] = useState(0);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadSong();
  }, [id]);

  const loadSong = async () => {
    try {
      setLoading(true);
      const songData = await songAPI.getSong(id);
      setSong(songData);
      setCurrentAmount(songData.current_amount);
    } catch (error) {
      console.error('Error loading song:', error);
      toast.error('Failed to load song');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustCurrentAmount = async (adjustment) => {
    const newAmount = Math.max(0, currentAmount + adjustment);
    try {
      await songAPI.updateSong(id, { current_amount: newAmount });
      setCurrentAmount(newAmount);
    } catch (error) {
      console.error('Error updating amount:', error);
      toast.error('Failed to update amount');
    }
  };

  const handleConfirmAmount = async () => {
    try {
      setConfirming(true);
      await songAPI.confirmAmountInFolder(id);
      toast.success('Amount confirmed!');
      navigate('/advanced?tab=folder');
    } catch (error) {
      console.error('Error confirming amount:', error);
      toast.error('Failed to confirm amount');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!song) {
    return (
      <PageContainer>
        <ErrorMessage>
          <h2>Song not found</h2>
          <p>The song you're looking for doesn't exist.</p>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/advanced?tab=folder')}>
        ← Back to Folder
      </BackButton>

      <SongTitle>{song.name}</SongTitle>

      <ControlsContainer>
        <AmountSection>
          <AmountControls>
            <AmountButton
              type="button"
              onClick={() => handleAdjustCurrentAmount(-1)}
            >
              −
            </AmountButton>
            <AmountDisplay>{currentAmount}</AmountDisplay>
            <AmountButton
              type="button"
              primary
              onClick={() => handleAdjustCurrentAmount(1)}
            >
              +
            </AmountButton>
          </AmountControls>
          <ConfirmButton 
            onClick={handleConfirmAmount}
            disabled={confirming}
          >
            {confirming ? 'Confirming...' : 'Confirm Amount'}
          </ConfirmButton>
        </AmountSection>
      </ControlsContainer>
    </PageContainer>
  );
}

export default FolderSongPage;
